import React, { useEffect, useRef, useState } from 'react';
import { Goal, goalService } from '../../services/goalService';
import { RunningTimer, useTimers } from '../../contexts/TimerContext';
import { useToast } from '../../contexts/ToastContext';
import { useTranslation } from '../../i18n';
import { getErrorMessage } from '../../types/error';
import { trackEvent } from '../../utils/analytics';
import { clickableProps } from '../../utils/a11y';
import { parseBackendDateToLocal } from '../../utils/dateTime';
import { formatDurationShort } from '../../utils/format';
import { elapsedSecondsSince, formatClock } from '../../utils/timer';
import Icon from '../Icon';
import './HomeContinue.css';

/** Rows on Home before "See all" opens the Ongoing page. */
export const CONTINUE_PREVIEW = 3;

/** A todo that was started and is not finished (T-153, as native T-152). */
export interface ContinueItem {
  todoId: string;
  title: string;
  /** Null for a quick todo with no goal: it opens Timely instead. */
  subGoalId: string | null;
  /** "Goal › Sub-goal", when the todo belongs to one. */
  path: string | null;
  running: boolean;
  startedAt: string | null;
  /** Time recorded so far, without the running stretch. */
  spentSeconds: number;
  /** When work on it last stopped, for the order of the paused ones. */
  lastWorked: string | null;
}

const time = (value: string | null): number => (value ? parseBackendDateToLocal(value)?.getTime() ?? 0 : 0);
const newestFirst = (a: string | null, b: string | null) => time(b) - time(a);

/**
 * Started, unfinished todos: every running timer first (also quick todos with no goal), the
 * newest start first; then todos with time recorded but stopped, the most recently worked on
 * first. No request of its own: the goal tree and the running timers.
 */
export const continueItems = (goals: Goal[], running: RunningTimer[]): ContinueItem[] => {
  const inTree = new Map<string, ContinueItem>();
  goals.forEach((goal) => {
    (goal.subGoals ?? []).forEach((subGoal) => {
      (subGoal.todos ?? []).forEach((todo) => {
        if (todo.completed) return;
        const spent = todo.totalTimeSpent ?? 0;
        if (!todo.isStarted && spent <= 0) return;
        inTree.set(todo.id, {
          todoId: todo.id,
          title: todo.title,
          subGoalId: subGoal.id,
          path: `${goal.title} › ${subGoal.title}`,
          running: !!todo.isStarted,
          startedAt: todo.startedAt ?? null,
          spentSeconds: spent,
          lastWorked: todo.stoppedAt ?? todo.startedAt ?? null,
        });
      });
    });
  });

  const timerIds = new Set(running.map((timer) => timer.todoId));
  const live: ContinueItem[] = [
    // The running timers know the true start (the open time entry), and include quick todos
    ...running.map((timer) => {
      const known = inTree.get(timer.todoId);
      return {
        todoId: timer.todoId,
        title: known?.title ?? timer.title,
        subGoalId: known?.subGoalId ?? null,
        path: known?.path ?? null,
        running: true,
        startedAt: timer.startedAt,
        spentSeconds: known?.spentSeconds ?? 0,
        lastWorked: timer.startedAt,
      };
    }),
    // Started in the tree but not in the timers yet (they reload every minute)
    ...Array.from(inTree.values()).filter((item) => item.running && !timerIds.has(item.todoId)),
  ].sort((a, b) => newestFirst(a.startedAt, b.startedAt));

  const paused = Array.from(inTree.values())
    .filter((item) => !item.running && !timerIds.has(item.todoId))
    .sort((a, b) => newestFirst(a.lastWorked, b.lastWorked));

  return [...live, ...paused];
};

/**
 * True when the goal tree disagrees with the running timers about a todo it holds: started in
 * the tree but not running, or running but not started in the tree.
 */
export const treeOutOfSync = (goals: Goal[], running: RunningTimer[]): boolean => {
  const timerIds = new Set(running.map((timer) => timer.todoId));
  return goals.some((goal) => (goal.subGoals ?? []).some((subGoal) => (subGoal.todos ?? []).some(
    (todo) => !todo.completed && !!todo.isStarted !== timerIds.has(todo.id),
  )));
};

/**
 * Keeps the goal tree in step with timers started or stopped anywhere (the running timer bar,
 * Timely, a Continue row; T-154): when the running set changes and the tree no longer agrees,
 * [reload] fetches it again. Only on a change of the running set, so a tree that is ahead of
 * the timers (they refresh every minute) does not reload in a loop.
 */
export const useTreeTimerSync = (goals: Goal[], running: RunningTimer[], reload?: () => void) => {
  const key = running.map((timer) => timer.todoId).sort().join(',');
  const previous = useRef<string | null>(null);
  const latest = useRef({ goals, running, reload });
  latest.current = { goals, running, reload };
  useEffect(() => {
    const first = previous.current === null;
    previous.current = key;
    if (first) return;
    const { goals: tree, running: timers, reload: load } = latest.current;
    if (load && treeOutOfSync(tree, timers)) load();
  }, [key]);
};

const LiveClock: React.FC<{ startedAt: string }> = ({ startedAt }) => {
  const [elapsed, setElapsed] = useState(() => elapsedSecondsSince(startedAt));
  useEffect(() => {
    setElapsed(elapsedSecondsSince(startedAt));
    const interval = window.setInterval(() => setElapsed(elapsedSecondsSince(startedAt)), 1000);
    return () => window.clearInterval(interval);
  }, [startedAt]);
  return <span className="home-continue-clock">{formatClock(elapsed)}</span>;
};

/**
 * One started todo: a live dot or a paused mark, the title and where it belongs, the time, and
 * Stop or Start. Also used by the Ongoing page. The goal tree follows through
 * [useTreeTimerSync]; [onChanged] reloads it after a forgotten timer's time is corrected.
 */
export const ContinueRow: React.FC<{ item: ContinueItem; onChanged?: () => void }> = ({ item, onChanged }) => {
  const { t, language } = useTranslation();
  const toast = useToast();
  const timers = useTimers();
  const [busy, setBusy] = useState(false);
  const running = item.running && !!item.startedAt;
  const spent = formatDurationShort(item.spentSeconds, language);

  const open = () => {
    window.location.hash = item.subGoalId
      ? `#/subgoals/${item.subGoalId}/todos/${item.todoId}`
      : '#/timely';
  };

  const toggle = async () => {
    setBusy(true);
    try {
      if (running) {
        await timers.stopTimer(item.todoId, { startedAt: item.startedAt, onCorrected: onChanged });
      } else {
        await goalService.startTodo(item.todoId);
        trackEvent('timer_started', { surface: 'home' });
        await timers.refresh();
      }
    } catch (err: unknown) {
      toast.error(getErrorMessage(err, t(running ? 'errors.stopTodo' : 'errors.startTodo')));
    } finally {
      setBusy(false);
    }
  };

  const label = running ? t('todos.item.tooltips.stop') : t('todos.item.tooltips.start');
  return (
    <li className="home-continue-row">
      <div
        className="home-continue-row__main"
        {...clickableProps(open, 'link')}
        aria-label={[item.title, running ? t('home.continue.runningNow') : spent && t('home.continue.spent', { time: spent })]
          .filter(Boolean)
          .join(', ')}
      >
        <span className={`home-continue-mark${running ? ' is-running' : ''}`} aria-hidden="true">
          {running ? <span className="home-continue-dot" /> : <Icon name="pause" />}
        </span>
        <div className="home-continue-text">
          <bdi className="home-continue-title">{item.title}</bdi>
          {item.path && <bdi className="home-continue-path">{item.path}</bdi>}
        </div>
        {running && item.startedAt
          ? <LiveClock startedAt={item.startedAt} />
          : spent && <span className="home-continue-spent">{spent}</span>}
      </div>
      <button
        type="button"
        className={`home-continue-toggle${running ? ' is-stop' : ''}`}
        onClick={() => { void toggle(); }}
        disabled={busy}
        aria-label={`${label}: ${item.title}`}
        title={label}
      >
        {/* A filled square, as on the running timer bar (Icon has no stop glyph) */}
        {running ? <span className="home-continue-stop" aria-hidden="true" /> : <Icon name="play" />}
      </button>
    </li>
  );
};

interface HomeContinueProps {
  items: ContinueItem[];
  onChanged?: () => void;
}

/**
 * Home "Continue" (T-153, as native T-152), above Today: what you started and did not finish,
 * at most three rows with "See all" to the Ongoing page. Hidden when there is nothing.
 */
const HomeContinue: React.FC<HomeContinueProps> = ({ items, onChanged }) => {
  const { t } = useTranslation();
  if (items.length === 0) return null;
  return (
    <section className="home-continue" aria-labelledby="home-continue-title">
      <div className="home-continue-header">
        <Icon name="playCircle" className="home-continue-icon" />
        <h3 id="home-continue-title">{t('home.continue.title')}</h3>
        {items.length > CONTINUE_PREVIEW && (
          <a className="home-continue-seeall" href="#/ongoing">
            {t('home.continue.seeAll', { count: items.length })}
          </a>
        )}
      </div>
      <ul className="home-continue-list">
        {items.slice(0, CONTINUE_PREVIEW).map((item) => (
          <ContinueRow key={item.todoId} item={item} onChanged={onChanged} />
        ))}
      </ul>
    </section>
  );
};

export default HomeContinue;
